import { useRef } from 'react'

export type UploadFile = {
  uid?: string
  name: string
  status?: 'uploading' | 'done' | 'error' | 'removed'
  [key: string]: any
}

type Props = {
  accept?: string
  disabled?: boolean
  multiple?: boolean
  fileList?: UploadFile[]
  customRequest?: (options: { file: File }) => void
  onChange?: (info: { file: UploadFile }) => void
  onRemove?: (file: UploadFile) => void
  showUploadList?: { showDownloadIcon?: boolean; showRemoveIcon?: boolean }
  children?: React.ReactNode
  'data-cy'?: string
  'data-testid'?: string
}

const FileUpload = ({
  accept,
  disabled = false,
  multiple = false,
  fileList = [],
  customRequest,
  onChange,
  onRemove,
  showUploadList,
  children,
  'data-cy': dataCy,
  'data-testid': dataTestId,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }
    const uploadFile: UploadFile = { name: file.name, status: 'uploading' }
    onChange?.({ file: uploadFile })
    customRequest?.({ file })
    // reset input so the same file can be re-selected
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <div
      className="updated-component"
      data-cy={dataCy}
      data-testid={dataTestId}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={handleFileChange}
      />
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        className={
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }
      >
        {children}
      </div>
      {showUploadList !== undefined && fileList.length > 0 && (
        <ul className="mt-2 space-y-1">
          {fileList.map((file, i) => (
            <li
              key={file.uid ?? i}
              className="flex items-center justify-between text-sm text-gray-700 bg-gray-50 px-2 py-1 rounded"
            >
              <span className="truncate max-w-xs">{file.name}</span>
              {showUploadList?.showRemoveIcon !== false && (
                <button
                  type="button"
                  className="ml-2 text-gray-400 hover:text-red-500"
                  onClick={() => onRemove?.(file)}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default FileUpload
